import { useEffect, useState } from "react";
import { Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { useAuth, STAFF_ROLES } from "../../context/AuthContext";
import { fmtDate } from "../../lib/format";
import { PageHeader, Loading, Money, StatusBadge, Modal, Field, ErrBox, Empty } from "../../components/common";

const TABS = [["upcoming", "Upcoming"], ["scheduled", "Scheduled"], ["completed", "Completed"], ["failed", "Failed"], ["past_due", "Past Due"]];

export default function Payments() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [open, setOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", due_date: "", tax_type: "AZ TPT", method: "ACH Debit", notes: "" });
  const [statusModal, setStatusModal] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "PAID", confirmation_number: "", failure_reason: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isStaff = user && STAFF_ROLES.includes(user.role);

  const load = () => API.get("/payments").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const schedule = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await API.post("/payments", payForm);
      toast.success("Payment scheduled — it will show as paid only after confirmation");
      setOpen(false); setPayForm({ amount: "", due_date: "", tax_type: "AZ TPT", method: "ACH Debit", notes: "" });
      load();
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  const updateStatus = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await API.patch(`/payments/${statusModal.id}`, statusForm);
      toast.success("Payment updated");
      setStatusModal(null); load();
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  if (!data) return <Loading label="Loading payment center" />;
  const list = data.grouped[tab] || [];

  return (
    <div data-testid="payments-page">
      <PageHeader title="Tax Payment Center" sub="Payments are only marked paid when confirmed — never just because they were scheduled."
        right={<button className="btn-navy" onClick={() => setOpen(true)} data-testid="schedule-payment-btn"><Plus className="h-4 w-4" /> Schedule Payment</button>} />

      <div className="mb-5 flex flex-wrap gap-1 rounded-md border border-slate-200 bg-white p-1" data-testid="payment-tabs">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`}
            className={`rounded px-3 py-1.5 text-sm font-semibold ${tab === v ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            {l} <span className="ml-1 rounded-full bg-slate-200/60 px-1.5 text-xs">{(data.grouped[v] || []).length}</span>
          </button>
        ))}
      </div>

      {list.length === 0 ? <Empty title={`No ${tab.replace("_", " ")} payments`} /> : (
        <div className="space-y-3">
          {list.map((p) => (
            <div key={p.id} className="card-gov flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`payment-${p.case_number}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-slate-400">{p.case_number}</span>
                  <StatusBadge status={p.status} />
                </div>
                <div className="mt-1 font-semibold text-navy">{p.authority} · {p.tax_type}</div>
                <div className="text-xs text-slate-500">Due {fmtDate(p.due_date)}{p.payment_date ? ` · Paid ${fmtDate(p.payment_date)}` : ""}{p.scheduled_date ? ` · Scheduled ${fmtDate(p.scheduled_date)}` : ""}</div>
                {p.confirmation_number && <div className="mt-1 text-xs text-slate-500">Confirmation: <span className="font-mono font-semibold text-emerald-700">{p.confirmation_number}</span></div>}
                {p.status === "FAILED" && (
                  <div className="mt-2 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="payment-failure-reason">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span><strong>Payment failed.</strong> Reason: {p.failure_reason || "Unknown"}. Required action: update your payment method or contact support.</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4">
                <Money cents={p.amount_cents} exact className="text-xl font-bold text-navy" />
                {isStaff && !["PAID", "CANCELLED"].includes(p.status) && (
                  <button className="btn-outline-gov px-3 py-1.5 text-xs" onClick={() => { setStatusModal(p); setStatusForm({ status: "PAID", confirmation_number: "", failure_reason: "" }); setErr(""); }} data-testid={`update-payment-${p.case_number}`}>
                    Update Status
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Schedule Tax Payment">
        <form onSubmit={schedule} className="space-y-4" data-testid="schedule-payment-form">
          <Field label="Amount ($) *"><input className="input-gov money" inputMode="decimal" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} required data-testid="pay-amount" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Due Date *"><input type="date" className="input-gov" value={payForm.due_date} onChange={(e) => setPayForm({ ...payForm, due_date: e.target.value })} required data-testid="pay-due" /></Field>
            <Field label="Tax Type">
              <select className="input-gov" value={payForm.tax_type} onChange={(e) => setPayForm({ ...payForm, tax_type: e.target.value })} data-testid="pay-tax-type">
                {["AZ TPT", "Federal Estimated", "Payroll (941)", "AZ Withholding", "Other"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Payment Method">
              <select className="input-gov" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} data-testid="pay-method">
                {["ACH Debit", "Government Portal", "Check", "Card", "Wire"].map((m) => <option key={m}>{m}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Notes"><input className="input-gov" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} data-testid="pay-notes" /></Field>
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">Scheduling does not move money. Payments require authorization and are marked paid only when confirmed by the authority or provider.</p>
          <ErrBox msg={err} />
          <button className="btn-navy w-full" disabled={busy} data-testid="pay-submit">{busy ? "Scheduling…" : "Schedule Payment"}</button>
        </form>
      </Modal>

      <Modal open={!!statusModal} onClose={() => setStatusModal(null)} title={`Update Payment ${statusModal?.case_number || ""}`}>
        <form onSubmit={updateStatus} className="space-y-4" data-testid="payment-status-form">
          <Field label="New Status">
            <select className="input-gov" value={statusForm.status} onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })} data-testid="payment-status-select">
              {["SCHEDULED", "PROCESSING", "PAID", "FAILED", "CANCELLED"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          {statusForm.status === "PAID" && (
            <Field label="Confirmation Number *" hint="Required — proof the payment actually completed">
              <input className="input-gov font-mono" value={statusForm.confirmation_number} onChange={(e) => setStatusForm({ ...statusForm, confirmation_number: e.target.value })} required data-testid="payment-confirmation" />
            </Field>
          )}
          {statusForm.status === "FAILED" && (
            <Field label="Failure Reason"><input className="input-gov" value={statusForm.failure_reason} onChange={(e) => setStatusForm({ ...statusForm, failure_reason: e.target.value })} data-testid="payment-failure-input" placeholder="e.g. Bank authorization declined" /></Field>
          )}
          <ErrBox msg={err} />
          <button className="btn-navy w-full" disabled={busy} data-testid="payment-status-submit">{busy ? "Saving…" : "Update Payment"}</button>
        </form>
      </Modal>
    </div>
  );
}
